<template>
  <div class="user-menu">
    <button
      ref="triggerRef"
      type="button"
      class="user-menu__trigger"
      @click="toggle">
      <img
        v-if="user?.avatarUrl"
        :src="user.avatarUrl"
        class="user-menu__avatar"
        :alt="userEmail">
      <span
        v-else
        class="user-menu__avatar user-menu__avatar--placeholder">
        <i class="fa-solid fa-user" />
      </span>
      <span class="user-menu__name">
        {{ userEmail }}
        <span v-if="isAdminPage && user?.impersonating" class="user-menu__impersonating-badge">
          impersonating
        </span>
      </span>
      <i
        class="fa-solid fa-chevron-down user-menu__chevron"
        :class="{ 'user-menu__chevron--open': isOpen }" />
    </button>

    <DropdownContent>
      <div v-if="user?.impersonating" class="user-menu__impersonating">
        <i class="fa-solid fa-user-secret" />
        Impersonating
      </div>
      <div class="user-menu__header">
        <span class="user-menu__email">{{ user?.email }}</span>
        <button class="user-menu__copy-btn" @click="copyEmail">
          <i class="fa-regular fa-copy" />
        </button>
      </div>
      <AppLink
        v-if="showWallet"
        v-tooltip="'Go to Billing'"
        href="/billing/edit"
        class="user-menu__wallet"
        @click="close">
        <i class="fa-regular fa-wallet user-menu__wallet-icon" />
        <div class="user-menu__wallet-balances">
          <span>{{ formatNumber(organization.keywordWalletBalance) }} Keyword Credits</span>
          <span>{{ formatNumber(organization.geogridWalletBalance) }} GeoGrid Credits</span>
        </div>
      </AppLink>
      <div class="user-menu__items">
        <template v-for="item in userMenuItems" :key="item.label">
          <div v-if="item.dividerBefore" class="app-dropdown__divider" />
          <button
            v-if="item.onClick"
            type="button"
            class="app-dropdown__item user-menu__action-item"
            @click="runAction(item)">
            <i
              v-if="item.icon"
              :class="item.icon"
              class="user-menu__item-icon" />
            {{ item.label }}
            <span v-if="item.unreadDot" class="user-menu__unread-dot" />
          </button>
          <AppLink
            v-else
            :href="item.href"
            :data-method="item.method"
            :target="item.external ? '_blank' : undefined"
            :rel="item.external ? 'noopener noreferrer' : undefined"
            class="app-dropdown__item"
            @click="close">
            <component
              :is="item.iconComponent"
              v-if="item.iconComponent"
              class="user-menu__item-icon" />
            <i
              v-else-if="item.icon"
              :class="item.icon"
              class="user-menu__item-icon" />
            {{ item.label }}
            <i
              v-if="item.external"
              class="fa-solid fa-arrow-up-right-from-square user-menu__external-icon" />
          </AppLink>
          <div v-if="item.dividerAfter" class="app-dropdown__divider" />
        </template>
      </div>
    </DropdownContent>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useGlobalStore } from '@/stores/global'
import { useChangelogStore } from '@/stores/changelog'
import useDropdown from '@/composables/dropdown'
import { copyToClipboardWithAnimation } from '@/common/copyToClipboard'
import { formatNumber } from '@/helpers/format'
import userMenuConfig from '@/config/userMenu'

const globalStore = useGlobalStore()
const changelogStore = useChangelogStore()

const triggerRef = ref(null)
const { DropdownContent, toggle, isOpen, close } = useDropdown(triggerRef, {
  placement: 'bottom-end'
})

const isAdminPage = window.location.pathname.startsWith('/admin')
const user = computed(() => globalStore.user)
const userEmail = computed(() => user.value?.email)
const organization = computed(() => globalStore.organization)

const showWallet = computed(() => user.value?.adminPrivileges &&
  !organization.value.isLinda &&
  organization.value?.keywordWalletBalance !== null)

const userMenuItems = computed(() => userMenuConfig
  .filter((item) => !item.show || item.show(globalStore))
  .map((item) => ({
    label: item.label,
    href: typeof item.href === 'function' ? item.href(globalStore) : item.href,
    icon: item.icon,
    iconComponent: item.iconComponent,
    method: item.method,
    external: item.external,
    dividerBefore: item.dividerBefore,
    dividerAfter: item.dividerAfter,
    onClick: item.onClick,
    unreadDot: item.unreadDot ? changelogStore.unread : false
  })))

const runAction = (item) => {
  close()
  item.onClick()
}

const copyEmail = (e) => {
  copyToClipboardWithAnimation({
    element: e.currentTarget,
    textToCopy: user.value?.email
  })
}
</script>
